import React, { useCallback, useMemo } from 'react';
import { List, Spin } from 'antd';
import { MenuCategoriesListItem } from 'widgets/menu';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { setUiCartItemModal, setUiCommonModal } from 'app/store/reducers/ui.reducer';
import { IMenuItemWithCategoryNameAndType } from 'entities/Menu/Menu.models';

const getStatus = (err: any) => err?.status ?? err?.statusCode ?? err?.data?.statusCode ?? err?.response?.status;
type ViewState = 'LIST' | 'WELCOME' | 'NOT_FOUND' | 'ERROR';

export const SearchMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const { menu, menuError, loading, rawSearch, customerStoreId, storeId } = useAppSelector((state) => ({
    menu: state.menu.menuCollection?.categoriesToAsset ?? [],
    menuError: state.menu.menuCollectionError,
    loading: state.menu.menuCollectionLoading || state.menu.menuAddOnsLoading,
    rawSearch: state.search.debouncedValue,
    customerStoreId: state.customer.customerModel?.store?.id ?? null,
    storeId: state.store.storeModel?.id ?? null,
  }));

  const q = useMemo(() => (rawSearch ?? '').trim(), [rawSearch]);
  const hasSearch = q.length > 0;
  const hasResults = menu.length > 0;

  const status = useMemo(() => getStatus(menuError), [menuError]);
  const isNotFound = status === 404;

  const viewState: ViewState = useMemo(() => {
    if (!hasSearch) {
      return 'WELCOME';
    }
    if (loading) {
      return hasResults ? 'LIST' : 'WELCOME';
    }
    if (isNotFound) {
      return 'NOT_FOUND';
    }
    if (hasResults) {
      return 'LIST';
    }
    if (!menuError) {
      return 'NOT_FOUND';
    }
    return 'ERROR';
  }, [hasSearch, loading, hasResults, isNotFound, menuError]);

  const isDefaultMenu = !customerStoreId && !storeId;

  const handleMenuItemClick = useCallback(
    (item: IMenuItemWithCategoryNameAndType) => {
      if (isDefaultMenu) {
        dispatch(setUiCommonModal({ isVisible: true }));
        return;
      }
      dispatch(setUiCartItemModal({ isVisible: true, menuItem: item }));
    },
    [dispatch, isDefaultMenu]
  );

  const renderCategory = useCallback(
    (category: any) => (
      <MenuCategoriesListItem category={category} isDefaultMenu={isDefaultMenu} isRowView onMenuItemClick={handleMenuItemClick} />
    ),
    [handleMenuItemClick, isDefaultMenu]
  );

  const content = useMemo(() => {
    switch (viewState) {
      case 'LIST':
        return <List dataSource={menu} renderItem={renderCategory} />;
      case 'NOT_FOUND':
        return <h3 className="search-menu__container__title">{"Oops :( We couldn't find this product in the menu"}</h3>;
      case 'ERROR':
        return <h3 className="search-menu__container__title">Something went wrong</h3>;
      case 'WELCOME':
      default:
        return (
          <>
            <h3 className="search-menu__container__title">Masayang Araw!</h3>
            <span className="search-menu__container__desc">Search for your favourite Minute Burger product</span>
          </>
        );
    }
  }, [viewState, menu, renderCategory]);

  return (
    <div className="search-menu">
      <div className="search-menu__container">
        <Spin spinning={loading} className="mt-2">
          {content}
        </Spin>
      </div>
    </div>
  );
};
